import { ChangeDetectionStrategy, Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Calendar } from 'primeng/calendar';

const DISABLE_PREVIOUS_ACTION = {
  month: (_: MuCalendarComponent) => {
    return _.invalidMinYear(_.currentYear - 1)
  },
  year: (_: MuCalendarComponent) => {
    const years = _.yearPickerValues();

    return years.includes(_.minDate.getFullYear());
  },
  date: (_: MuCalendarComponent) => {
    const date = new Date(_.currentYear, _.currentMonth - 1, 31);

    return (date < _.minDate);
  }
}

const DISABLE_NEXT_ACTION = {
  month: (_: MuCalendarComponent) => {
    return _.invalidMaxYear(_.currentYear + 1)
  },
  year: (_: MuCalendarComponent) => {
    const years = _.yearPickerValues();

    return years.includes(_.maxDate.getFullYear());
  },
  date: (_: MuCalendarComponent) => {
    const date = new Date(_.currentYear, _.currentMonth + 1, _.maxDate.getDate());

    return (date > _.maxDate);
  }
}

@Component({
  selector: 'app-mu-calendar',
  templateUrl: './mu-calendar.component.html',
  styleUrls: ['./mu-calendar.component.sass'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MuCalendarComponent),
    multi: true
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MuCalendarComponent extends Calendar implements OnInit, OnDestroy, ControlValueAccessor {
  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /**
   * valid if the month is valid, if it has a minimum or maximum date
   * @param {number} month month in number 0 to 11
   * @returns {boolean}
   */
  isMonthValid(month: number): boolean {
    if (!this.minDate && !this.maxDate) {
      return true;
    }

    const minMonth = this.minDate?.getMonth();
    const minYear = this.minDate?.getFullYear();
    const maxMonth = this.maxDate?.getMonth();
    const maxYear = this.maxDate?.getFullYear();

    if (this.minDate && this.maxDate) {
      return !this.validateMonthWithMinAndMaxDate(month, minYear, minMonth, maxYear, maxMonth);
    }

    return this.validateMonthWithMinDate(month, minYear, minMonth)
      || this.validateMonthWithMaxDate(month, maxYear, maxMonth);
  }

  /**
   * Validate if a month is valid between two dates
   * @param month
   * @param minYear
   * @param minMonth
   * @param maxYear
   * @param maxMonth
   * @returns
   */
  validateMonthWithMinAndMaxDate(month: number, minYear: number, minMonth: number, maxYear: number, maxMonth: number): boolean {
    if (minYear === this.currentYear && maxYear === this.currentYear) {
      if ((maxMonth === month || month === minMonth) || (maxMonth > month && minMonth < month)) {
        return false;
      }
    }

    if (minYear === this.currentYear && maxYear > this.currentYear) {
      if (minMonth <= month) {
        return false;
      }
    }

    if (minYear < this.currentYear && maxYear === this.currentYear) {
      if (maxMonth >= month) {
        return false;
      }
    }

    if (minYear < this.currentYear && maxYear > this.currentYear) {
      return false;
    }

    return true;
  }

  /**
   * Validate if a month is valid when there is a minimum date
   * @param month
   * @param minYear
   * @param minMonth
   * @returns
   */
  validateMonthWithMinDate(month: number, minYear: number, minMonth: number): boolean {
    return( minYear < this.currentYear) || (minMonth <= month && minYear === this.currentYear);
  }

  /**
   * Validate if a month is valid when there is a maximum date
   * @param month
   * @param maxYear
   * @param maxMonth
   * @returns
   */
  validateMonthWithMaxDate(month: number, maxYear: number, maxMonth: number): boolean {
    return (maxYear > this.currentYear) || (maxMonth >= month && maxYear === this.currentYear);
  }


  /**
   * valid if the year is valid when there is a minimum date
   * @param {number} year
   * @returns {boolean}
   */
  invalidMinYear(year: number): boolean {
    if (!this.minDate) {
      return false;
    }

    return (this.minDate.getFullYear() > year);
  }

  /**
   * valid if the year is valid when there is a maximum date
   * @param {number} year
   * @returns {boolean}
   */
  invalidMaxYear(year: number): boolean {
    if (!this.maxDate) {
      return false;
    }

    return (this.maxDate.getFullYear() < year);
  }

  /**
   * disable the previous action if there is a minimum date
   * @returns {boolean}
   */
  disabledPreviousAction(): boolean {
    if (!this.minDate) {
      return false;
    }

    return DISABLE_PREVIOUS_ACTION[this.currentView](this);
  }

  /**
   * disable the next action if there is a maximum date
   * @returns {boolean}
   */
  disabledNextAction(): boolean {
    if (!this.maxDate) {
      return false;
    }

    return DISABLE_NEXT_ACTION[this.currentView](this);
  }
}
